// Keep editorial photos ready for a quick vertical scroll. Native lazy loading
// remains the fallback; the browser still chooses each responsive rendition.
(() => {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const canWarm = () => !connection || (!connection.saveData && !['slow-2g', '2g', '3g'].includes(connection.effectiveType));
  if (!('IntersectionObserver' in window) || !canWarm()) return;

  let started = false;
  const start = () => {
    if (started || !canWarm()) return;
    started = true;
    const main = document.querySelector('main');
    if (!main) return;
    const pending = new Set();
    const observed = new WeakSet();
    const distance = Math.min(3000, Math.max(1800, window.innerHeight * 2));
    let active = 0;
    const eligible = (img) => img.isConnected && img.loading === 'lazy' &&
      img.hasAttribute('src') && !img.hasAttribute('data-src') &&
      !/^auto(?:\s*,|\s*$)/.test(img.sizes || '') &&
      !img.closest('product-card-carousel, scroll-carousel, slideshow-carousel, effect-carousel, video-media, hero-video, [hidden], [inert], [aria-hidden="true"]');
    const visibleColumn = (img) => {
      const rect = img.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < window.innerWidth &&
        rect.bottom >= -distance && rect.top <= window.innerHeight + distance &&
        getComputedStyle(img).visibility !== 'hidden';
    };
    const pump = () => {
      if (!canWarm() || document.hidden) return;
      const ordered = [...pending].filter(eligible).sort((a, b) => {
        const gap = img => Math.max(0, img.getBoundingClientRect().top - window.innerHeight, -img.getBoundingClientRect().bottom);
        return gap(a) - gap(b);
      });
      for (const img of ordered) {
        if (active >= 3) break;
        if (!eligible(img) || !visibleColumn(img)) continue;
        pending.delete(img);
        observer.unobserve(img);
        if (img.complete) continue;
        active++;
        let finished = false;
        const finish = () => {
          if (finished) return;
          finished = true;
          img.removeEventListener('load', finish);
          img.removeEventListener('error', finish);
          active--;
          if (img.naturalWidth && typeof img.decode === 'function') img.decode().catch(() => {});
          pump();
        };
        img.addEventListener('load', finish, { once: true });
        img.addEventListener('error', finish, { once: true });
        const rect = img.getBoundingClientRect();
        if (rect.top >= window.innerHeight && img.fetchPriority !== 'high') img.fetchPriority = 'low';
        img.loading = 'eager';
        if (img.complete) finish();
      }
    };
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting && eligible(entry.target)) pending.add(entry.target);
        else pending.delete(entry.target);
      }
      pump();
    }, { rootMargin: `${distance}px 0px`, threshold: 0 });
    const observe = root => {
      for (const img of root.querySelectorAll('img[loading="lazy"][src]')) {
        if (!observed.has(img) && eligible(img)) {
          observed.add(img);
          observer.observe(img);
        }
      }
    };
    observe(main);
    document.addEventListener('shopify:section:load', event => {
      if (main.contains(event.target)) observe(event.target);
    });
    document.addEventListener('visibilitychange', pump);
  };
  const afterLoad = () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 1000 });
    else start();
  };
  if (document.readyState === 'complete') afterLoad();
  else window.addEventListener('load', afterLoad, { once: true });
  window.addEventListener('scroll', start, { once: true, passive: true });
})();
